/**
 * @Author: Li, Yu Jia
 * @Date: 2020-01-13 11:47:35
 * @Last Modified by: Li, Yu Jia
 * @Last Modified time: 2021-07-08 15:27:00
 * @Usage: userimage.selectors.func
 */

import { defaultImageColor, userImageStep } from './../utils/constants';
import { getCurrentImprintAreaCode, getCurrentImprintAreaKey, getStudioItemType } from './../main/selectors';
import {
  getCurrentImprintFormat,
  getDefaultImprintColor,
  getImprintOptionLogoTypeCheck,
  getMultipleColorsSpecialCharge,
} from './../imprintarea/selectors';
import { getSelectedImage, getTempArt } from './../canvas/selectors';

import { colorType } from './../utils/constants';
import { createSelector } from 'reselect';
import { getFreeColor } from '../nextgen/selectors';
import { getImprintColors } from './../imprintarea/selectors';
import { getPmsUnitPrice } from '../charges/selectors';
import { getSelectedImprintColors } from './../imprintarea/selectors';
import { outsideUserImageMatch } from './../imprintarea/logics';
import { removeColorDuplicates } from './../imprintarea/logics';
import { studioItemType as studioItemTypeEnum } from './../main/constants';

const getSloganList = createSelector(
  (state) => state.sloganDict,
  (state, imprintAreaCode) => imprintAreaCode || getCurrentImprintAreaCode(state),
  (sloganDict, imprintAreaCode) => sloganDict[imprintAreaCode]
);
const getInnerOrOuterSlogan = createSelector(
  (state, isOutsideSlogan, imprintAreaCode) => getSloganList(state, imprintAreaCode),
  (state, isOutsideSlogan) => isOutsideSlogan,
  (sloganDict, isOutsideSlogan) => {
    if (sloganDict && sloganDict.slogans) {
      if (isOutsideSlogan) {
        const newSloganList = sloganDict.slogans.filter((item) => item.isOutside && item.sloganReference !== 'NONE');
        return newSloganList;
      } else {
        const newSloganList = sloganDict.slogans.filter((item) => !item.isOutside && item.sloganReference !== 'NONE');
        return newSloganList;
      }
    } else {
      return [];
    }
  }
);
const getStockArtCategories = createSelector(
  (state) => state.stockArtDict,
  (state) => state.stockArtCategoryDict,
  getCurrentImprintAreaCode,
  (stockArtDict, stockArtCategoryDict, imprintAreaCode) => {
    const cateGoryList = stockArtCategoryDict[imprintAreaCode] || [];
    const stockArtList = stockArtDict[imprintAreaCode] || {};
    return cateGoryList.map((x) => {
      return { ...x, inventory: stockArtList[x.id] !== undefined };
    });
  }
);

const getStockArts = createSelector(
  (state) => state.stockArtDict,
  getCurrentImprintAreaCode,
  (stockArtDict, imprintAreaCode) => stockArtDict[imprintAreaCode] || {}
);

const getVerseList = createSelector(
  (state) => state.verseList,
  (verseList) => verseList || []
);

const getSentimentList = createSelector(
  (state) => state.sentimentList,
  (sentimentList) => sentimentList || []
);

const getSavedArts = createSelector(
  (state) => state.savedArtDict,
  (state) => state.vendor,
  getCurrentImprintFormat,
  (state) => state.user,
  (savedArtDict, vendor, format, user) => {
    //key:imprintAreaCode-processType
    if (!user || !format) {
      return { count: 0, list: [] };
    }
    const key = `${format.imprintAreaCode}-${format.processType}`;
    return vendor.allowSavedArt && savedArtDict[key]
      ? {
          count: savedArtDict[key].count,
          list: savedArtDict[key].list,
        }
      : { count: 0, list: {} };
  }
);
const noSharedArt = { count: 0, list: [], searchable: false };
const getSharedArts = createSelector(
  (state) => state.sharedArtDict,
  (state) => state.vendor,
  (state) => state.parameters,
  (state, formatkey) => getCurrentImprintFormat(state, formatkey),
  (sharedArtDict, vendor, parameters, format) => {
    if (!parameters || !format || !(vendor.allowSharedArt && sharedArtDict[format.key])) return noSharedArt;
    return {
      count: sharedArtDict[format.key].length,
      list: sharedArtDict[format.key],
      searchable: !!sharedArtDict[format.key].find((x) => x.searchKeyword),
    };
  }
);
const getRecentArts = createSelector(
  (state) => state.recentArtDict,
  (state) => state.vendor,
  (state, formatkey) => formatkey || getCurrentImprintAreaKey(state),
  (state) => state.imprintAreaDict,
  (recentArtDict, vendor, formatkey, imprintAreaDict) => {
    const imprintAreaCode = imprintAreaDict[formatkey] && imprintAreaDict[formatkey].imprintAreaCode;
    //key:imprintAreaCode
    if (!imprintAreaCode) {
      return { count: 0, list: [] };
    }
    return vendor.allowRecentArt && recentArtDict[imprintAreaCode]
      ? { count: recentArtDict[imprintAreaCode].length, list: recentArtDict[imprintAreaCode] }
      : { count: 0, list: [] };
  }
);

const getUserImageAccess = createSelector(
  (state) => state.vendor,
  (vendor) => {
    return {
      slogan: vendor.allowSlogans,
      upload: vendor.allowLogoUpload,
      stock: vendor.allowStockArt,
      shared: vendor.allowSharedArt,
      saved: vendor.allowSavedArt,
      recent: vendor.allowRecentArt,
      verse: vendor.allowShowVerses,
    };
  }
);
const getTempUserImage = createSelector(
  (state, imprintAreaKey) => getTempArt(state, imprintAreaKey),
  (tempUserImage) => {
    return tempUserImage ? tempUserImage.data : null;
  }
);

const getSelectedUserImage = createSelector(getSelectedImage, (selectedUserImage) => {
  return selectedUserImage ? selectedUserImage.data : null;
});

/**
 * use for (screen)digital items
 */
const getSelectedColor = createSelector(
  getStudioItemType,
  getSelectedUserImage,
  getTempUserImage,
  getFreeColor,
  (studioItemType, singleUserImage, tempUserImage, freeColor) => {
    const selectedUserImage = singleUserImage || tempUserImage;
    const colors = selectedUserImage && selectedUserImage.colors;

    if (studioItemType === studioItemTypeEnum.digital) {
      return colors && colors.length > 0
        ? colors
        : [{ colorReference: defaultImageColor.rgb, cmyk: defaultImageColor.cmyk }];
    } else {
      if (freeColor && colors) {
        return [
          ...colors.filter((f) => f.colorId === freeColor.colorId),
          ...colors.filter((f) => f.colorId !== freeColor.colorId),
        ];
      } else {
        return colors;
      }
    }
  }
);

/**
 * use for NonDigital items
 */
const getSelectedImageColorForScene7 = createSelector(
  (state) => state,
  (_, formatkey) => formatkey,
  (state, formatkey) => {
    const findNormal =
      state.imageDict &&
      state.imageDict[formatkey] &&
      state.imageDict[formatkey].find((x) => !outsideUserImageMatch(x.data));

    return findNormal && findNormal.data
      ? findNormal.data.colors
      : (() => {
          const images = state.imageDict[formatkey];
          if (
            images &&
            images.length > 0 &&
            images[0].data &&
            images[0].data.colors &&
            images[0].data.colors.length > 0
          ) {
            return [...images[0].data.colors];
          } else {
            return [getDefaultImprintColor(state)];
          }
        })();
  }
);

const getAllColorsInUse = createSelector(
  (state) => state,
  (state, formatkey) => formatkey || getCurrentImprintAreaKey(state),
  (_0, _1, notIncludeCurrentImage) => notIncludeCurrentImage,
  (state, formatkey, notIncludeCurrentImage) => {
    const colorlist = getImprintColors(state, formatkey);
    const freeColor = getFreeColor(state);
    const selectedImprintColors = getSelectedImprintColors(state, formatkey, false, notIncludeCurrentImage);
    const colorsInUse = selectedImprintColors.map((x) => {
      if (x.colorType === colorType.Pms) {
        return Object.assign({}, x, { colorDescription: `PMS: ${x.colorId}` });
      } else {
        const _clr = colorlist.find((y) => y.colorId === x.colorId);
        return _clr || x;
      }
    });
    let inUseColors = removeColorDuplicates(colorsInUse || []);
    //-- free color to be first
    if (freeColor && inUseColors.length > 0 && inUseColors[0].colorId !== freeColor.colorId) {
      inUseColors = [
        ...inUseColors.filter((f) => f.colorId === freeColor.colorId),
        ...inUseColors.filter((f) => f.colorId !== freeColor.colorId),
      ];
    }

    return inUseColors;
  }
);

export const getAllowPmsColor = createSelector(getStudioItemType, getImprintColors, (studioItemType, imprintColors) => {
  if (studioItemType === studioItemTypeEnum.digital) {
    return true;
  }

  const allowPmsColor =
    imprintColors &&
    imprintColors.length > 0 &&
    imprintColors.find((x) => x.colorType === colorType.Pms && x.colorId === '');

  return allowPmsColor;
});

export const getRecentlyUsedColors = (state) => {
  const allColors = state.nextgenUi.recentlyUsedColors;

  const removeDupliatedColors = allColors.filter((item, index, arr) => {
    return arr.findIndex((v) => v.colorReference === item.colorReference) === index;
  });

  return removeDupliatedColors;
};

const getSelectedGuid = createSelector(getTempArt, (tempUserImage) => {
  return tempUserImage ? tempUserImage.ui.guid : '';
});
const getApplyNotice = createSelector(
  (state) => state.applyNotice,
  (applyNotice) => applyNotice
);
const getApplyNoticeStatus = createSelector(
  (state) => state.applyNotice,
  (applyNotice) => applyNotice.status
);
const getApplyNoticePrompt = createSelector(
  (state) => state.applyNotice,
  (applyNotice) => applyNotice.instance || applyNotice.prompt
);
export const getApplyNoticeScroll = createSelector(
  (state) => state.applyNotice,
  (applyNotice) => applyNotice.scroll
);
const getSharedArtDict = createSelector(
  (state) => state.sharedArtDict,
  (sharedArtDict) => sharedArtDict
);
const getSavedArtDict = createSelector(
  (state) => state.savedArtDict,
  (savedArtDict) => savedArtDict
);
const getUserImageClone = createSelector(
  (state) => state.userImageClone,
  (userImageClone) => userImageClone && userImageClone.data
);
const getImageElementClone = createSelector(
  (state) => state.userImageClone,
  (userImageClone) => userImageClone
);

export const getTotalSavedAndShardArtCount = createSelector(
  getSavedArts,
  getSharedArts,
  (saved, shared) => saved.count + shared.count
);

export {
  getInnerOrOuterSlogan,
  getSloganList,
  getSharedArtDict,
  getSavedArtDict,
  getStockArtCategories,
  getStockArts,
  getVerseList,
  getSentimentList,
  getSavedArts,
  getSharedArts,
  getRecentArts,
  getUserImageAccess,
  getTempUserImage,
  getSelectedUserImage,
  getSelectedGuid,
  getApplyNotice,
  getApplyNoticeStatus,
  getSelectedColor,
  getUserImageClone,
  getApplyNoticePrompt,
  getSelectedImageColorForScene7,
  getAllColorsInUse,
  getImageElementClone,
};

export const getAllowUserImageIcons = createSelector(
  getUserImageAccess,
  getSavedArts,
  getSharedArts,
  getRecentArts,
  getStockArtCategories,
  (state) => getInnerOrOuterSlogan(state, false),
  (state) => getImprintOptionLogoTypeCheck(state, getCurrentImprintAreaKey(state)),
  getCurrentImprintFormat,
  (access, saved, shared, recent, stockCategoryList, innerSlogan, logoOptionType, currentImprintFormat) => {
    const allowUpload = access.upload && logoOptionType;
    const allowStock = access.stock && logoOptionType && stockCategoryList && stockCategoryList.length > 0;
    const allowSlogan = access.slogan && innerSlogan.length > 0;
    const allowRecent = logoOptionType && recent.count > 0;
    let allowSavedOrShared = false;
    const hasSavedArt = access.saved && saved.count > 0;
    const hasSharedArt = access.shared && shared.count > 0;

    if (logoOptionType) {
      if (hasSavedArt && saved.list && saved.list[0]) {
        allowSavedOrShared = true;
      } else if (!hasSavedArt && hasSharedArt) {
        allowSavedOrShared = true;
      }
    }

    return [
      allowUpload && userImageStep.Upload,
      allowStock && userImageStep.Stock,
      allowSlogan && userImageStep.Slogan,
      allowRecent && userImageStep.Recent,
      allowSavedOrShared && userImageStep.Saved,
    ].filter(Boolean);
  }
);

export const onlyOneSavedAndShardArtCheck = createSelector(
  getTotalSavedAndShardArtCount,
  (totalSavedAndShardArtCount) => totalSavedAndShardArtCount === 1
);

export const onlyOneSloganCheck = createSelector(
  (state, isOutsideSlogan, imprintAreaCode) => getInnerOrOuterSlogan(state, isOutsideSlogan, imprintAreaCode),
  (sloganList) => sloganList.length === 1
);

export const onlyOneVerseCheck = createSelector(getVerseList, (verselist) => verselist.length === 1);

export const onlyOneSentimentCheck = createSelector(getSentimentList, (sentimentlist) => sentimentlist.length === 1);

export const getAllSharedArtSearchOpitons = createSelector(
  (state, imprintAreaKey) => getSharedArts(state, imprintAreaKey || getCurrentImprintAreaKey),
  (sharedArts) => {
    const allSharedArtSearchOpiton = sharedArts.list.map((art) => {
      const searchArray = (art.searchKeyword && art.searchKeyword.split(' ').filter((x) => x.length > 0)) || [];
      return {
        name: art.displayName || art.description,
        queryTargetArr: [art.displayName, art.description, ...searchArray],
      };
    });
    return allSharedArtSearchOpiton;
  }
);

export const getAllArtWorkOriginalPmsColors = createSelector(
  getCurrentImprintAreaKey,
  getStudioItemType,
  (state) => state.imageDict,
  (imprintAreakey, currentStudioItemType, imageDict) => {
    let colors = [];
    if (currentStudioItemType === studioItemTypeEnum.screenPrintedDigital) {
      const imageList = imageDict[imprintAreakey];
      if (imageList) {
        colors = imageList.map((image) => (image.ui.pmsColors ? image.ui.pmsColors : [])).flat();
        colors = removeColorDuplicates(colors || []);
      }
    }
    return colors;
  }
);
export const getColorChargeTotal = createSelector(
  getMultipleColorsSpecialCharge,
  getPmsUnitPrice,
  getAllColorsInUse,
  (multipleColorsSpecialCharge, pmsUnitPrice, allColorsInUse) => {
    let chargeTotal = 0;
    if (multipleColorsSpecialCharge) {
      chargeTotal = Number(multipleColorsSpecialCharge.replace('$', '')) * (allColorsInUse.length - 1);
    }
    if (pmsUnitPrice) {
      const pmsColors = allColorsInUse.filter((color) => color.colorType === colorType.Pms);
      chargeTotal += pmsUnitPrice * pmsColors.length;
    }
    return chargeTotal;
  }
);
